import { ITreatmentPlan } from '@dataTypes/treatmentPlan.types'
import { default as axios } from './axiosConfig'

import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type TreatmentPlanResponseData = {
  treatment_plan?: ITreatmentPlan[]
}
// Define the types for the response structure
export type GetAllTreatPlanResponse = ApiResponse<TreatmentPlanResponseData>
export type UpdateTreatPlanResponse = ApiResponse<TreatmentPlanResponseData>
export type TreatPlanResponse = ApiResponse<Record<string, unknown>>
export type IsSlotAvailableResponse = ApiResponse<any>
export interface TreatPlanRes {
  statusCode: number
  message: string
  data: {
    treatment_plan: ITreatmentPlan[] | []
  }
}

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_TREAT_PLAN')
const headers = {
  'Content-Type': 'application/json',
}

// Function to get access token
export const getTreatmentPlanByPatient = async (
  patientId: string,
): Promise<GetAllTreatPlanResponse> => {
  try {
    const { data, status } = await axios.get<GetAllTreatPlanResponse>(
      `${BUSINESS_LAYER_API}/api/v1/treatplans/patient/${patientId}`,
      {
        headers,
      },
    )
    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const createTreatmentPlan = async (
  treatmentPlan: ITreatmentPlan,
): Promise<TreatPlanResponse> => {
  try {
    const { tp_id, phases, ...rest } = treatmentPlan

    const sanitizedTreatmentPlan = {
      ...rest,
      phases:
        phases?.map((phase) => ({
          ...phase,
          items:
            phase.items?.map(
              ({ item_id, phase_id, ...itemRest }) => itemRest,
            ) ?? [],
        })) ?? [],
    }

    const { data, status } = await axios.post<TreatPlanResponse>(
      `${BUSINESS_LAYER_API}/api/v1/treatplans`,
      sanitizedTreatmentPlan,
      { headers },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 201) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const updateTreatmentPlanById = async (
  treatmentPlanId: string,
  treatmentPlan: Partial<ITreatmentPlan>,
): Promise<UpdateTreatPlanResponse> => {
  try {
    const { tp_id, phases, ...rest } = treatmentPlan

    const sanitizedTreatmentPlan = {
      ...rest,
      ...(tp_id && tp_id !== '' ? { tp_id } : {}),
      phases:
        phases?.map((phase) => ({
          ...phase,
          ...(phase.phase_id && phase.phase_id !== ''
            ? { phase_id: phase.phase_id }
            : {}),
          items:
            phase.items?.map(({ item_id, phase_id, ...itemRest }) => ({
              ...itemRest,
              ...(item_id && item_id !== '' ? { item_id } : {}),
            })) ?? [],
        })) ?? [],
    }

    const { data, status } = await axios.put<UpdateTreatPlanResponse>(
      `${BUSINESS_LAYER_API}/api/v1/treatplans`,
      { tp_id: treatmentPlanId, data: sanitizedTreatmentPlan },
      { headers },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const deleteTreatmentPlanById = async (
  tp_id: string,
): Promise<TreatPlanResponse> => {
  try {
    const { data, status } = await axios.delete<TreatPlanResponse>(
      `${BUSINESS_LAYER_API}/api/v1/treatplans/${tp_id}`,
      { headers },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}
